import React, { useState } from 'react';
import { useCarouselContext } from '../../context/CarouselContext';
import { useAssetActions } from '../../hooks/asset/useAssetActions';

interface ThumbnailImageProps {
  image: any;
  index: number;
  tag?: string;
  selectedImageIndex: number;
  handleGoTo: (index: number) => void;
}

const getAssetKeysFromAssetId = (assetId: string): { pk: string | null; sk: string | null } => {
  // Guard against null or undefined URLs
  if (!assetId) {
    return { pk: null, sk: null };
  }

  try {    
    if (!assetId || !assetId.includes('-')) {
      console.error("Could not find a valid asset identifier in the URL:", assetId);
      return { pk: null, sk: null };
    }

    // The SK is the timestamp part after the '-'
    const sk = assetId.split('-')[1];
    
    // The PK is a formatted string including the full asset identifier
    const pk = `asset#${assetId}`;

    if (!sk) {
        console.error("Could not extract SK from asset identifier:", assetId);
        return { pk: null, sk: null };
    }

    return { pk, sk };
  } catch (error) {
    console.error("Failed to parse asset keys from URL:", assetId, error);
    return { pk: null, sk: null };
  }
};

const ThumbnailImage = React.forwardRef<HTMLImageElement, ThumbnailImageProps>(
  ({ image, index, tag, selectedImageIndex, handleGoTo }, ref) => {
    const isSelected = selectedImageIndex === index;
    const [hasError, setHasError] = useState(false);
    const { carouselAssetId } = useCarouselContext();
    const { reprocessAsset } = useAssetActions();

    const handleImageError = () => {
      if (hasError) return;
      
      setHasError(true);
      const { pk, sk } = getAssetKeysFromAssetId(carouselAssetId);

      if (pk && sk) {
        console.log(`Thumbnail failed for ${pk}. Triggering regeneration.`);
        reprocessAsset(pk, sk, 'thumbnails');
      }
    };
    
    const sharedStyles: React.CSSProperties = {
      height: isSelected ? '95%' : '80%',
      aspectRatio: '16 / 9',
      border: isSelected ? '1.5px solid var(--ums-accent-red)' : '1px solid transparent',
      borderRadius: 4,
      cursor: 'pointer',
    };

    return !hasError && image ? (
      <img
        ref={ref}
        src={image}
        alt={`Selector ${index}`}
        onClick={() => handleGoTo(index)}
        onError={handleImageError}
        draggable={false}
        style={{
          ...sharedStyles,
          objectFit: 'cover',
        }}
      />
    ) : (
      <div
        onClick={() => handleGoTo(index)}
        draggable={false}
        style={{
          ...sharedStyles,
          backgroundColor: 'white',
          color: '#666',
          fontSize: '0.75rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 500,
          textAlign: 'center',
          padding: '4px',
        }}
      >
        <div>No Thumbnail</div>
        {tag && <div style={{ marginTop: 1, fontSize: '0.75rem', color: '#aaa' }}>{tag}</div>}
      </div>
    );
  }
);

export default ThumbnailImage;
